import { ConfigsService, LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { AccountService, AnalyticsService, REDIRECTIONS, TRACKING_TAGS as TAGS } from '@sm360/widget-commons-general';
import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';

import ShowroomService from 'commons-vehicle/services/ShowroomService/ShowroomService.js';
import QuoteService from 'commons-vehicle/Quote/services/QuoteService/QuoteService.js';
import SaveNewQuoteModelV25 from 'commons-vehicle/Quote/models/SaveNewQuoteModelV25';
import UpdateQuoteModelV25 from 'commons-vehicle/Quote/models/UpdateQuoteModelV25';

import VehicleService from 'commons-vehicle/Vehicle/services/VehicleService/VehicleService.js';
import TradeInsService from 'commons-vehicle/services/TradeInsService';

// SHOWROOM V2.5
class AddToGarageV25 {
    constructor() {
        this.createPublicQuote = this.createPublicQuote.bind(this);
        this.addToGarageOnClick = this.addToGarageOnClick.bind(this);
        this.buyOnlineOnClick = this.buyOnlineOnClick.bind(this);
        this.updateQuote = this.updateQuote.bind(this);
        this.addToGarage = this.addToGarage.bind(this);
        this.isQuoteInGarage = this.isQuoteInGarage.bind(this);
        this.signUpCallbacks = {};
    }

    createPublicQuote(SaveNewQuoteModel, config) {
        if (!(Object.getPrototypeOf(SaveNewQuoteModel) === Object.getPrototypeOf(new SaveNewQuoteModelV25()))) {
            throw new Error('createPublicQuote expecting instance of SaveNewQuoteModelV25');
        }

        return new Promise((resolve) => {
            if (AccountService.isAuthenticated()) {
                return TradeInsService.loadTradeInVehicle().then((tradeIn) => {
                    return resolve(tradeIn);
                });
            }
            return resolve(null);
        }).then((tradeIn) => {
            const tradeInAmount = tradeIn && tradeIn.estimation ? tradeIn.estimation.amount : 0;

            const SAVE_AS_PUBLIC = true;

            return ShowroomService.saveQuoteV25(
                SaveNewQuoteModel.set('tradeIn', tradeInAmount),
                SAVE_AS_PUBLIC,
                config
            ).then((quoteId) => {
                return quoteId;
            });
        });
    }

    // Function is called from WebAuto theme directly
    // isMarketPlace param passes only from Dilawri marketplace theme
    addToGarageOnClick(SaveNewQuoteModel, goToCheckout = false, config, isMarketPlace = false) {
        if (!(SaveNewQuoteModel instanceof SaveNewQuoteModelV25)) {
            throw new Error('addToGarageOnClick expecting instance of SaveNewQuoteModelV25');
        }

        if (!AccountService.isAuthenticated()) {
            if (window.Login) {
                this.signUpCallbacks.onClose = (params) => {
                    const newAccount = params && params.newAccount;
                    this.addToGarage(SaveNewQuoteModel, goToCheckout, newAccount, config, isMarketPlace);
                    return true;
                };
                window.Login.widgets.Login.services.LoginLightService.openSignupModal(this.signUpCallbacks, {
                    submitAccountCreationLead: false,
                });
            }
        } else {
            this.addToGarage(SaveNewQuoteModel, goToCheckout, null, config, isMarketPlace);
        }
    }

    buyOnlineOnClick(SaveNewQuoteModel, config) {
        const GO_TO_CHECKOUT = true;
        this.addToGarageOnClick(SaveNewQuoteModel, GO_TO_CHECKOUT, config);
    }

    // Create or update (if quoteId available in URL) a quote in the garage
    addOrUpdateQuote(QuoteModelV25, newAccount, config) {
        const accountId = AccountService.getUserAccountId() || 0;
        const quote = QuoteService.isQuoteInGarageV25(QuoteModelV25.id);
        const saveNewQuoteV25 = new SaveNewQuoteModelV25().setModelValuesFromQuoteModelV25(QuoteModelV25);
        if (accountId) {
            // If quote saved in Garage
            if (quote) {
                // Update it
                const updateQuoteV25 = new UpdateQuoteModelV25().setModelValuesFromQuoteModelV25(
                    QuoteModelV25.set('accountId', quote.accountId)
                        .set('leadId', quote.leadId)
                        .set('quoteRevision', quote.quoteRevision)
                );
                return this.updateQuote(updateQuoteV25, config);
            }
            // Else create a private quote
            return this.addToGarageFromVDP(saveNewQuoteV25, newAccount, config);
        }
        // Create a public quote
        return ShowroomService.saveQuoteV25(saveNewQuoteV25, true, config);
    }

    updateQuote(UpdateQuoteModel, config) {
        if (!(UpdateQuoteModel instanceof UpdateQuoteModelV25)) {
            throw new Error('updateQuote expecting instance of UpdateQuoteModelV25');
        }

        const { accountId, leadId } = UpdateQuoteModel;

        return new Promise((resolve, reject) => {
            if (accountId && AccountService.isAuthenticated()) {
                if (!leadId) return reject(new Error(LocaleService.t('QUOTE.UPDATE_ERROR')));
                return ShowroomService.updateQuoteV25(UpdateQuoteModel, config).then((quoteId) => {
                    this.dispatchAddToGarage();
                    return resolve(quoteId);
                });
            }

            return reject(new Error('No account id or user is not authenticated'));
        });
    }

    addToGarage(SaveNewQuoteModel, goToCheckout = false, newAccount, config, isMarketPlace = false) {
        const accountId = AccountService.getUserAccountId();

        if (accountId) {
            ShowroomService.saveQuoteV25(SaveNewQuoteModel.set('accountId', accountId), false, config)
                .then((quoteId) => {
                    return ShowroomService.loadQuote(quoteId);
                })
                .then((quote) => {
                    if (quote.vehicle) {
                        AnalyticsService.gaTrigger(
                            VehicleService.isVehicleNew(quote.vehicle)
                                ? TAGS.ADD_TO_GARAGE_CTA_NEW(quote.vehicle.model)
                                : TAGS.ADD_TO_GARAGE_CTA_USED(quote.vehicle.model)
                        );
                    }

                    this.dispatchAddToGarage();

                    const newQuote = quote.vehicleId === '0' ? quote.set('vehicleId', 0) : quote;

                    const leadRequest = new LeadSubmissionBuilder(config, 'Add To Garage')
                        .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
                        .addQuote(newQuote)
                        .addVehicle(newQuote);

                    if (newAccount) {
                        leadRequest.setIsAccountCreation();
                        leadRequest.addOptInEmail(newAccount.optInEmail);
                        leadRequest.addContactInformation(
                            newAccount.firstname,
                            newAccount.lastname,
                            newAccount.homePhone,
                            newAccount.email1,
                            newAccount.contactId
                        );
                    }

                    leadRequest.submit().then((response) => {
                        const { leadId } = response;
                        ShowroomService.saveQuoteV25(quote.set('leadId', leadId), false, config);

                        if (goToCheckout) {
                            this.goToCheckout(newQuote, config);
                        } else {
                            this.goToVdp(newQuote.id, config, isMarketPlace);
                        }
                    });
                })
                .catch((error) => {
                    throw error;
                });
        }

        return true;
    }

    // Used by the VDPs to add a vehicle to the garage
    addToGarageFromVDP(SaveNewQuoteModel, newAccount, config) {
        const accountId = AccountService.getUserAccountId();
        if (accountId) {
            return ShowroomService.saveQuoteV25(SaveNewQuoteModel.set('accountId', accountId), false, config)
                .then((quoteId) => ShowroomService.loadQuote(quoteId))
                .then((quote) => {
                    if (quote.vehicle) {
                        AnalyticsService.gaTrigger(
                            VehicleService.isVehicleNew(quote.vehicle)
                                ? TAGS.ADD_TO_GARAGE_CTA_NEW(quote.vehicle.model)
                                : TAGS.ADD_TO_GARAGE_CTA_USED(quote.vehicle.model)
                        );
                    }

                    this.dispatchAddToGarage();

                    const newQuote = quote.vehicleId === '0' ? quote.set('vehicleId', 0) : quote;

                    const leadRequest = new LeadSubmissionBuilder(config, 'Add To Garage')
                        .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
                        .addQuote(newQuote)
                        .addVehicle(newQuote);

                    if (newAccount) {
                        leadRequest.setIsAccountCreation();
                        leadRequest.addOptInEmail(newAccount.optInEmail);
                        leadRequest.addContactInformation(
                            newAccount.firstname,
                            newAccount.lastname,
                            newAccount.homePhone,
                            newAccount.email1,
                            newAccount.contactId
                        );
                    }

                    return leadRequest.submit().then(() => {
                        return Promise.resolve(quote.id);
                    });
                })
                .catch((error) => {
                    throw error;
                });
        }

        return true;
    }

    dispatchAddToGarage() {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event('sm360.widgets.garage.added'));
        }
    }

    goToCheckout(quote, config) {
        const locale = LocaleService.getSimpleLocale();
        const windowProxy = WindowProxy.get();
        const checkoutUrl = REDIRECTIONS[locale].checkout;
        const historyUrl = quote.buildUrl(windowProxy.location.href);
        const { orgUnitId } = config || {};
        const fullCheckoutUrl = quote.buildUrl(checkoutUrl, orgUnitId);

        windowProxy.history.replaceState({ quote }, 'tradeIn', historyUrl);

        if (ConfigsService.isInMobileApp()) {
            windowProxy.location.href =
                `ca.solutionsmedias360.showroom-${ConfigsService.getWebsiteId()}://` +
                `?action=login&redirectUrl=${encodeURIComponent(fullCheckoutUrl)}`;
        } else {
            windowProxy.location.href = fullCheckoutUrl;
        }
    }

    goToVdp(quoteId, config, isMarketPlace) {
        const $window = WindowProxy.get();
        const event = new CustomEvent('add-to-garage:success', {
            bubbles: true,
            cancelable: false,
            detail: { quoteId },
        });
        $window.document.dispatchEvent(event);
        // we need to refresh the page because the quoteId has changed
        // when adding vehicle to garage
        // TODO: figure out how do stay on the page without refreshing
        if ($window) {
            ShowroomService.searchQuoteV25(quoteId, config).then((quoteV25) => {
                const asShowroomV25 = true;
                window.location.href = quoteV25.getVDPUrl(asShowroomV25, isMarketPlace);
            });
        }
    }

    isQuoteInGarage(quoteId, isMultiDealerGarage = false) {
        let response = false;

        if (!AccountService.isAuthenticated()) return false;

        return ShowroomService.loadQuotesV25(isMultiDealerGarage).then((res) => {
            if (res) {
                res.forEach((e) => {
                    if (e.id === quoteId) response = true;
                });
            }
            return response;
        });
    }
}

export default AddToGarageV25;
